import React, { useState, useEffect } from "react";
import "../styles/pages/OtpVerification.css";
import "../styles/pages/StepColorOverrides.css"; // Added step color overrides
import "../styles/components/FixedStepper.css"; // For fixed position stepper
import "../styles/pages/OtpVerificationOverrides.css"; // Added custom overrides for layout
import "../styles/pages/OtpScrollFixOverrides.css"; // Added scroll fixes for mobile
import "../styles/pages/OtpMobileOverrides.css"; // Added high-specificity mobile overrides
import "../styles/pages/OtpFinalFixes.css"; // Final overrides with highest specificity
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Loader from "../components/Loader";
import { sendOtp, verifyOtp, fetchCompanyData } from "../services/otpService";
import { findCompanyByMobile, mergeCompanyData } from "../services/companyDataService";
import sessionTracker from "../utils/sessionTracker";
import gtmService from "../utils/gtmService";
import CacheCleanup from "../utils/cacheCleanup";

const OtpVerification = () => {
    const [phoneNumber, setPhoneNumber] = useState("");
    const [countryCode, setCountryCode] = useState("+91"); // Default to India country code
    const [otp, setOtp] = useState(["", "", "", "", "", ""]); // Empty OTP fields
    const [remainingTime, setRemainingTime] = useState(300); // 5 minutes = 300 seconds - OTP validity time
    const [resendCooldown, setResendCooldown] = useState(0); // Separate resend cooldown timer (30 seconds)
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [usingTestMode, setUsingTestMode] = useState(false); // No longer default to test mode
    const [consentChecked, setConsentChecked] = useState(false); // Changed to unchecked by default
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [customerName, setCustomerName] = useState("Customer"); // Default customer name
    const [actualOtp, setActualOtp] = useState(null); // Track the actual OTP from server
    const [bypassMode, setBypassMode] = useState(false); // 🚀 BYPASS MODE FOR TESTING

    // Initialize session tracking
    useEffect(() => {
        const initializeTracking = async () => {
            await sessionTracker.updateStage('otp');
        };
        initializeTracking();
        
        // Track page view in GTM for GA4
        gtmService.trackPageView('otp_verification', 'OTP Verification', window.location.pathname);
        
        // Track user journey stage
        gtmService.trackUserJourney('otp_page_loaded', {
            page_type: 'verification',
            step_number: 1
        });

        // ENHANCED: Use aggressive cache cleanup when OTP page loads - fresh start for new session
        CacheCleanup.initializeForNewSession('initial-load');

        // Cleanup function to remove modal-open class when component unmounts
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, []);

    // Handle bypass banner body padding
    useEffect(() => {
        if (bypassMode) {
            document.body.style.paddingTop = '50px';
        } else {
            document.body.style.paddingTop = '0px';
        }
        
        // Cleanup on unmount
        return () => {
            document.body.style.paddingTop = '0px';
        };
    }, [bypassMode]);

    // Improved scroll handling for smoother experience
    useEffect(() => {
        // Only apply scroll handling if not on mobile
        if (!isMobile) {
            let scrollPosition = window.pageYOffset;
            let isUserScrolling = false;
            let scrollTimeout;

            // Store scroll position when user scrolls
            const handleScroll = () => {
                isUserScrolling = true;
                scrollPosition = window.pageYOffset;

                // Reset the user scrolling flag after a short delay
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    isUserScrolling = false;
                }, 100);
            };

            // Function to restore scroll position only if not user-initiated
            const preventAutoScroll = () => {
                // Only prevent auto scrolling if user isn't actively scrolling
                if (!isUserScrolling) {
                    window.scrollTo({
                        top: scrollPosition,
                        behavior: "auto", // Use auto instead of smooth for less visual disruption
                    });
                }
            };

            // Add event listeners with passive true for better performance
            window.addEventListener("scroll", handleScroll, { passive: true });

            // Check less frequently and only when needed
            const intervalId = setInterval(preventAutoScroll, 500);

            // Cleanup on unmount
            return () => {
                window.removeEventListener("scroll", handleScroll);
                clearTimeout(scrollTimeout);
                clearInterval(intervalId);
            };
        }
    }, [isMobile]);

    // Check if on mobile device
    useEffect(() => {
        const checkIfMobile = () => {
            const width = window.innerWidth;
            setIsMobile(width <= 768);
        };

        // Check initially
        checkIfMobile();

        // Add resize listener
        window.addEventListener("resize", checkIfMobile);

        // Clean up
        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);

    // Apply direct style overrides for mobile view
    useEffect(() => {
        if (isMobile) {
            // Force styles with JavaScript for absolute control
            setTimeout(() => {
                try {
                    // Apply styles to Bumrah image with stronger override
                    const bumrahImages = document.querySelectorAll(
                        ".bumrah-image, .bumrah-container img"
                    );
                    bumrahImages.forEach((img) => {
                        img.style.setProperty("max-width", "340px", "important");
                        img.style.setProperty("width", "340px", "important");
                        img.style.setProperty("height", "auto", "important");
                        img.style.setProperty("transform", "scale(1)", "important");
                        img.style.setProperty("margin-top", "-65px", "important"); // No change
                        img.style.setProperty("position", "relative", "important");
                        img.style.setProperty("z-index", "1", "important");
                    });

                    // Apply stronger overrides to the Bumrah container too
                    const bumrahContainers =
                        document.querySelectorAll(".bumrah-container");
                    bumrahContainers.forEach((container) => {
                        container.style.setProperty("margin-top", "-35px", "important"); // No change
                        container.style.setProperty("margin-bottom", "-25px", "important");
                    });

                    // Apply styles to form container with stronger override
                    const formContainers = document.querySelectorAll(".form-container");
                    formContainers.forEach((container) => {
                        container.style.setProperty("margin-top", "-45px", "important"); // Changed from -60px to -45px (+15px shift down)
                        container.style.setProperty("position", "relative", "important");
                        container.style.setProperty("z-index", "10", "important");
                        container.style.setProperty("padding-top", "30px", "important"); // Added padding to create more space inside
                    });

                    
                } catch (err) {
                    // Style override error - silently continue
                }
            }, 100); // Short delay to ensure DOM is ready
        }
    }, [isMobile]);

    // Handle OTP modal initialization and cleanup
    useEffect(() => {
        if (isMobile) {
            if (showOtpInput) {
                // Modal is being shown - ensure it's properly initialized
                setTimeout(() => {                        try {
                            // Ensure there's only one modal
                            const modals = document.querySelectorAll(".otp-modal");
                            if (modals.length > 1) {
                                // Keep only the one with the proper ID
                                const mainModal = document.getElementById(
                                    "otp-verification-modal"
                                );
                                modals.forEach((modal) => {
                                    if (modal !== mainModal) {
                                        modal.remove();
                                    }
                                });
                            }

                            // Ensure there's only one close button
                            const closeButtons = document.querySelectorAll(".otp-modal-close");
                            if (closeButtons.length > 1) {
                                // Keep only the one with the proper ID
                                const mainButton = document.getElementById(
                                    "otp-modal-close-button"
                                );
                                closeButtons.forEach((button) => {
                                    if (button !== mainButton) {
                                        button.remove();
                                    }
                                });
                            }

                            // Ensure the close button is properly positioned
                            const closeButton = document.getElementById(
                                "otp-modal-close-button"
                            );
                            if (closeButton) {
                                closeButton.style.position = "absolute";
                                closeButton.style.top = "10px";
                                closeButton.style.right = "10px";
                                closeButton.style.zIndex = "10003";
                            }
                        } catch (err) {
                            // OTP modal initialization error - silently continue
                        }
                }, 50);
            } else {
                // Modal is being hidden - clean up any remnants
                setTimeout(() => {
                        try {
                            // Remove any duplicate close buttons that might have been created
                            const closeButtons = document.querySelectorAll(
                                ".otp-modal-close:not(#otp-modal-close-button)"
                            );
                            if (closeButtons.length > 0) {
                                closeButtons.forEach((button) => button.remove());
                            }

                            // Remove any duplicate modals that might exist
                            const modals = document.querySelectorAll(".otp-modal");
                            if (modals.length > 0) {
                                modals.forEach((modal) => modal.remove());
                            }
                        } catch (err) {
                            // OTP modal cleanup error - silently continue
                        }
                }, 100);
            }
        }
    }, [showOtpInput, isMobile]);

    // Handle phone number input change
    const handlePhoneNumberChange = (e) => {
        const value = e.target.value;
        // Only allow digits
        if (/^\d*$/.test(value)) {
            setPhoneNumber(value);
            
            // Clear any existing company data when phone number changes
            if (value !== phoneNumber) {
                sessionStorage.removeItem('companyData');
            }
        }
    };

    // Handle country code change
    const handleCountryCodeChange = (e) => {
        setCountryCode(e.target.value);
    };

    // Handle consent checkbox change (used with custom checkbox)
    const handleConsentChange = () => {
        const newCheckedState = !consentChecked;
        setConsentChecked(newCheckedState);

        // Clear the error message if user accepts the terms
        if (
            newCheckedState &&
            error === "Please accept the terms and conditions to proceed"
        ) {
            setError("");
        }
    };

    // Open consent modal
    const openConsentModal = (e) => {
        if (e) e.preventDefault();
        setShowConsentModal(true);

        // Prevent body scroll when modal is open
        document.body.classList.add('modal-open');

        // Ensure the consent modal appears above the OTP modal
        setTimeout(() => {
            const consentModal = document.querySelector(".consent-modal");
            if (consentModal) {
                consentModal.style.zIndex = "10005";
                // Ensure modal is properly centered
                consentModal.scrollTop = 0;
            }

            const consentModalContent = document.querySelector(
                ".consent-modal-content"
            );
            if (consentModalContent) {
                consentModalContent.style.zIndex = "10006";
            }
        }, 10);
    };

    // Close consent modal
    const closeConsentModal = () => {
        setShowConsentModal(false);
        // Re-enable body scroll
        document.body.classList.remove('modal-open');
    };

    // Accept consent from modal
    const acceptConsent = () => {
        setConsentChecked(true);
        setShowConsentModal(false);
        // Re-enable body scroll
        document.body.classList.remove('modal-open');

        // Clear the error message if it was about accepting terms
        if (error === "Please accept the terms and conditions to proceed") {
            setError("");
        }
    };

    // Decline consent from modal
    const declineConsent = () => {
        setConsentChecked(false);
        setShowConsentModal(false);
        // Re-enable body scroll
        document.body.classList.remove('modal-open');
    };

    // Handle OTP input change
    const handleOtpChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto focus next input
            if (value && index < 5) {
                document.getElementById(`otp-input-${index + 1}`).focus();
            }
        }
    };

    // Handle keydown for OTP inputs for better UX
    const handleKeyDown = (index, e) => {
        // If backspace is pressed and the current field is empty, focus on the previous field
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            document.getElementById(`otp-input-${index - 1}`).focus();
        }
    };

    // Start countdown timer
    const startCountdown = () => {
        setRemainingTime(300); // 5 minutes = 300 seconds for OTP validity
        const timer = setInterval(() => {
            setRemainingTime((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
    };

    // Start resend cooldown timer (30 seconds)
    const startResendCooldown = () => {
        setResendCooldown(30); // 30 seconds cooldown for resend button
        const timer = setInterval(() => {
            setResendCooldown((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
    };

    // Handle get OTP button click - NORMAL FLOW ONLY
    const handleGetOtp = async () => {
        try {
            setIsLoading(true);
            setError("");

            if (!phoneNumber || phoneNumber.length < 10) {
                setError("Please enter a valid phone number");
                setIsLoading(false);
                return;
            }
            
            // Check consent before proceeding
            if (!consentChecked) {
                setError("Please accept the terms and conditions to proceed");
                setIsLoading(false);
                return;
            }

            // IMMEDIATELY clear any existing company data when requesting OTP for new number
            sessionStorage.removeItem('companyData');

            // 🚀 BYPASS FOR TESTING - Only if bypass mode is active
            if (bypassMode) {
                console.log('🔄 BYPASS MODE: Skipping OTP verification for testing');
                
                // Store phone number in sessionStorage (normally done after OTP verification)
                sessionStorage.setItem("phoneNumber", `${countryCode} ${phoneNumber}`);
                
                // Store verification timestamp to track when the user was verified
                sessionStorage.setItem("verificationTimestamp", Date.now().toString());
                
                // Track successful OTP verification (simulated for bypass)
                await sessionTracker.trackOTPVerification(
                    phoneNumber,
                    countryCode,
                    'Verified',
                    1
                );
                
                // Track bypass in GTM for GA4
                gtmService.trackOTPEvent('bypass_test', phoneNumber, {
                    country_code: countryCode,
                    page_path: window.location.pathname,
                    verification_method: 'bypass'
                });
                
                // Show loading message for bypass
                setError("🔄 BYPASS MODE: Testing company data lookup...");
                
                // Wait a moment to show the message
                setTimeout(async () => {
                    // Directly perform company data lookup and navigate
                    await performCompanyDataLookup();
                    setIsLoading(false);
                }, 1500);
                
                return;
            }

            // ========== NORMAL OTP FLOW (When bypass is disabled) ==========
            // Regular OTP flow - Call the OTP service with the phone number
            const result = await sendOtp(phoneNumber, customerName);
            
            // Track OTP verification attempt
            await sessionTracker.trackOTPVerification(
                phoneNumber,
                countryCode,
                'Pending',
                1
            );
            
            // Track OTP request in GTM for GA4
            gtmService.trackOTPEvent('request', phoneNumber, {
                country_code: countryCode,
                page_path: window.location.pathname
            });
            
            // Show the OTP input section
            setShowOtpInput(true);
            startCountdown();
            startResendCooldown(); // Start the resend cooldown timer

            // Clear OTP fields
            setOtp(["", "", "", "", "", ""]);

            // If we got an OTP in the response (for testing or debugging)
            if (result.success && result.otp) {
                setActualOtp(result.otp); // Store the OTP for validation

                // For testing purposes only - in production we wouldn't auto-fill
                // Only set usingTestMode to true if we're in development environment
                if (process.env.NODE_ENV === "development") {
                    // Convert OTP string to array of characters
                    const otpChars = result.otp.split("");
                    setOtp(otpChars.concat(Array(6 - otpChars.length).fill("")));
                    setUsingTestMode(true);
                }
            } else {
                setUsingTestMode(false);
            }
        } catch (error) {
            // Handle specific error cases from OTP session manager
            if (error.response && error.response.status === 429) {
                setError("Too many OTP requests. Please wait before requesting again.");
            } else if (error.response && error.response.data && error.response.data.message) {
                setError(error.response.data.message);
            } else {
                setError("Failed to send OTP. Please try again.");
            }
            setShowOtpInput(false); // Hide OTP input on error
        } finally {
            // Don't set loading to false here for bypass mode - it's handled in the timeout
            if (!bypassMode || !phoneNumber || phoneNumber.length < 10 || !consentChecked) {
            setIsLoading(false);
            }
        }
    };

    // Handle resend OTP - Using real OTP service
    const handleResendOtp = async (e) => {
        e.preventDefault();

        try {
            setIsLoading(true);
            setError("");

            // Call the OTP service again
            const result = await sendOtp(phoneNumber, customerName);

            // Reset and start countdown
            startCountdown();
            startResendCooldown(); // Restart the resend cooldown timer

            // Clear OTP fields
            setOtp(["", "", "", "", "", ""]);

            // If we got an OTP in the response (for testing or debugging)
            if (result.success && result.otp) {
                setActualOtp(result.otp); // Store the OTP for validation

                // For testing purposes only - in production we wouldn't auto-fill
                // Only set usingTestMode to true if we're in development environment
                if (process.env.NODE_ENV === "development") {
                    // Convert OTP string to array of characters
                    const otpChars = result.otp.split("");
                    setOtp(otpChars.concat(Array(6 - otpChars.length).fill("")));
                    setUsingTestMode(true);
                }
            } else {
                setUsingTestMode(false);
            }
        } catch (error) {
            // Handle specific error cases from OTP session manager
            if (error.response && error.response.status === 429) {
                setError("Too many OTP requests. Please wait before requesting again.");
            } else if (error.response && error.response.data && error.response.data.message) {
                setError(error.response.data.message);
            } else {
                setError("Failed to resend OTP. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
        setError(""); // Clear error on resend
    };

    // Handle verify button click - Using real OTP service + Company Data Lookup
    const handleVerify = async () => {
        try {
            setIsLoading(true);
            setError("");

            const otpValue = otp.join("");

            if (!otpValue || otpValue.length !== 6) {
                setError("Please enter a valid 6-digit OTP");
                setIsLoading(false);
                return;
            }

            // Check if consent is checked
            if (!consentChecked) {
                setError("Please accept the terms and conditions to proceed");
                setIsLoading(false);

                // Open the consent modal automatically when verification fails due to consent
                // Add a small delay to ensure React has time to update state
                setTimeout(() => {
                    openConsentModal();

                    // Ensure the consent modal has a higher z-index than the OTP modal
                    const consentModal = document.querySelector(".consent-modal");
                    if (consentModal) {
                        consentModal.style.zIndex = "10005";
                    }

                    const consentModalContent = document.querySelector(
                        ".consent-modal-content"
                    );
                    if (consentModalContent) {
                        consentModalContent.style.zIndex = "10006";
                    }
                }, 50);

                return;
            }

            // If we have the actual OTP from the server (in development or test mode),
            // compare directly before making the API call
            if (actualOtp && otpValue === actualOtp) {
                // Store phone number in sessionStorage
                sessionStorage.setItem("phoneNumber", `${countryCode} ${phoneNumber}`);

                // Store verification timestamp to track when the user was verified
                sessionStorage.setItem("verificationTimestamp", Date.now().toString());

                // Track successful OTP verification
                await sessionTracker.trackOTPVerification(
                    phoneNumber,
                    countryCode,
                    'Verified',
                    1
                );
                
                // Track successful OTP verification in GTM for GA4
                gtmService.trackOTPEvent('verify_success', phoneNumber, {
                    country_code: countryCode,
                    page_path: window.location.pathname,
                    verification_method: 'direct_match'
                });

                // Show success message briefly before continuing with company data lookup
                setUsingTestMode(true); // Set to true to use green styling for success message
                setError("OTP verified successfully!");

                // COMPANY DATA LOOKUP - After successful OTP verification
                await performCompanyDataLookup();

                return;
            }

            // Call verify OTP service if direct comparison didn't succeed
            const result = await verifyOtp(phoneNumber, otpValue);

            if (result.success) {
                // Store phone number in sessionStorage
                sessionStorage.setItem("phoneNumber", `${countryCode} ${phoneNumber}`);

                // Store verification timestamp to track when the user was verified
                sessionStorage.setItem("verificationTimestamp", Date.now().toString());

                // Track successful OTP verification
                await sessionTracker.trackOTPVerification(
                    phoneNumber,
                    countryCode,
                    'Verified',
                    1
                );
                
                // Track successful OTP verification in GTM for GA4
                gtmService.trackOTPEvent('verify_success', phoneNumber, {
                    country_code: countryCode,
                    page_path: window.location.pathname,
                    verification_method: 'api_verification'
                });

                // Show success message briefly before continuing with company data lookup
                setUsingTestMode(true); // Set to true to use green styling for success message
                setError("OTP verified successfully!");

                // COMPANY DATA LOOKUP - After successful OTP verification
                await performCompanyDataLookup();

                return;
            } else {
                // Track failed OTP verification
                await sessionTracker.trackOTPVerification(
                    phoneNumber,
                    countryCode,
                    'Failed',
                    1
                );
                
                // Track failed OTP verification in GTM for GA4
                gtmService.trackOTPEvent('verify_failed', phoneNumber, {
                    country_code: countryCode,
                    page_path: window.location.pathname,
                    error_message: result.message || "Invalid OTP"
                });

                // Show error message for invalid OTP
                setUsingTestMode(false); // Set to false to use red styling for error message
                setError(result.message || "Invalid OTP. Please try again.");
            }
        } catch (error) {
            // Track failed OTP verification
            await sessionTracker.trackOTPVerification(
                phoneNumber,
                countryCode,
                'Failed',
                1
            );

            setUsingTestMode(false); // Set to false to use red styling for error message
            
            // Handle specific error cases from OTP session manager
            if (error.response && error.response.status === 400) {
                const errorData = error.response.data;
                if (errorData && errorData.errorCode === 'SESSION_INVALID') {
                    setError(errorData.message || "OTP session has expired. Please request a new OTP.");
                } else if (errorData && errorData.message) {
                    setError(errorData.message);
                } else {
                    setError("Invalid OTP. Please try again.");
                }
            } else if (error.response && error.response.data && error.response.data.message) {
                setError(error.response.data.message);
            } else {
                setError("Failed to verify OTP. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    // NEW FUNCTION: Perform company data lookup from both API and CSV
    const performCompanyDataLookup = async () => {
        try {
            console.log('🏢 ==========  COMPANY DATA LOOKUP DEBUG  ==========');
            console.log('🏢 Phone Number:', phoneNumber);
            console.log('🏢 Country Code:', countryCode);
            
            // ENHANCED FIX: Use aggressive cache cleanup utility
            CacheCleanup.forceCleanupWithValidation(phoneNumber, 3);
            
            console.log('🏢 Step 1: Calling API for company data...');
            // 1. Call API for company data
            const apiCompanies = await fetchCompanyData(phoneNumber);
            console.log('🏢 API Companies Result:', apiCompanies);
            
            console.log('🏢 Step 2: Checking CSV for company data...');
            // 2. Check CSV for company data  
            const csvCompanies = findCompanyByMobile(phoneNumber);
            console.log('🏢 CSV Companies Result:', csvCompanies);
            
            console.log('🏢 Step 3: Merging both sources...');
            // 3. Merge both sources
            const allCompanies = mergeCompanyData(apiCompanies, csvCompanies);
            console.log('🏢 Merged Companies Result:', allCompanies);
            
            console.log('🏢 Step 4: Checking if phone should have company data...');
            // 4. Check if this phone number should have company data
            const shouldHaveCompany = CacheCleanup.shouldHaveCompanyData(phoneNumber);
            console.log('🏢 Should Have Company Data (hardcoded list):', shouldHaveCompany);
            
            // 🔧 FIXED LOGIC: Trust API response over hardcoded list
            const hasActualCompanyData = allCompanies && allCompanies.length > 0;
            console.log('🏢 Has Actual Company Data (from API/CSV):', hasActualCompanyData);
            
            // 5. Store in sessionStorage if companies found (trust API over hardcoded list)
            if (hasActualCompanyData) {
                sessionStorage.setItem('companyData', JSON.stringify(allCompanies));
                console.log('🏢 ✅ Company data found and stored for phone:', phoneNumber);
                console.log('🏢 ✅ Stored Company Data:', allCompanies);
                console.log('🏢 ✅ BUSINESS CUSTOMER: API returned company data, storing regardless of hardcoded list');
            } else {
                // TRULY NEW CUSTOMER: No data from API or CSV
                CacheCleanup.forceCleanupWithValidation(phoneNumber, 5);
                console.log('🏢 ❌ NEW CUSTOMER: No company data found from API or CSV, cleaning for phone:', phoneNumber);
                
                // Additional safety check after cleanup
                setTimeout(() => {
                    if (!CacheCleanup.validateCleanState()) {
                        console.error('🚨 CRITICAL: Cache still dirty after cleanup for phone:', phoneNumber);
                        CacheCleanup.clearCompanyData(phoneNumber, 'final-safety-check');
                    }
                }, 1000);
            }

            console.log('🏢 Step 5: Final sessionStorage check...');
            console.log('🏢 Final sessionStorage companyData:', sessionStorage.getItem('companyData'));
            console.log('🏢 ========== COMPANY DATA LOOKUP COMPLETE ==========');

            // 6. Navigate to Add Details page after a short delay
            setTimeout(() => {
                console.log('🏢 Navigating to Add Details page...');
                navigate("/add-details");
            }, 1000);

        } catch (error) {
            console.error('🏢 ❌ ERROR in company lookup:', error);
            // Even on error, use aggressive cleanup for new numbers
            CacheCleanup.forceCleanupWithValidation(phoneNumber, 5);
            console.log('🏢 Error in company lookup, using aggressive cleanup for phone:', phoneNumber);
            
            // Continue to Add Details page even if company lookup fails
            setTimeout(() => {
                console.log('🏢 Navigating to Add Details page (after error)...');
                navigate("/add-details");
            }, 1000);
        }
    };

    // Handle changing phone number after OTP countdown expires
    const handleChangePhoneNumber = () => {
        setOtp(["", "", "", "", "", ""]);
        setShowOtpInput(false);
    };

    return (
        <div className="otp-page">
            {/* 🚀 BYPASS MODE BANNER */}
            {bypassMode && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    padding: '10px 20px',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 99999,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                    borderBottom: '2px solid #ff5722'
                }}>
                    🚀 BYPASS MODE ACTIVE - Testing Company Data Lookup (OTP Verification Skipped)
                    <button 
                        onClick={() => setBypassMode(false)}
                        style={{
                            marginLeft: '20px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Disable Bypass
                    </button>
                </div>
            )}
            {isLoading && !showOtpInput && <Loader />}
            {!isMobile && (
                <div className="left-section">
                    <img
                        src="/images/otpverfication/otpverfifcationbg.png"
                        alt="Background"
                        className="left-section-background"
                    />
                    <Link to="/">
                        <img
                            src="/images/LOGO.png?v=20241118"
                            alt="Game Changer Logo"
                            className="left-section-logo"
                        />
                    </Link>
                    <img
                        src="/images/otpverfication/Frame162427.png"
                        alt="Person"
                        className="left-section-person"
                    />
                </div>
            )}
            <div className={`right-section ${isMobile ? "mobile-view" : ""}`}>
                {isMobile ? (
                    <>
                        <div
                            style={{
                                position: "fixed",
                                top: "20px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "200px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: "9999",
                                padding: 0,
                                margin: 0,
                                boxSizing: "border-box",
                            }}
                        >
                            <Link
                                to="/"
                                style={{
                                    display: "block",
                                    width: "100%",
                                    padding: 0,
                                    margin: 0,
                                }}
                            >
                                <img
                                    src="/images/otpverfication/LOGO.png?v=20241118"
                                    alt="Game Changer Logo"
                                    style={{
                                        width: "100%",
                                        maxWidth: "200px",
                                        height: "auto",
                                        display: "block",
                                        margin: "0 auto",
                                        padding: 0,
                                    }}
                                />
                            </Link>
                        </div>
                        <div
                            style={{
                                width: "100%",
                                display: "flex !important",
                                flexDirection: "column !important",
                                alignItems: "center !important",
                                justifyContent: "center !important",
                                margin: "0 auto !important",
                                maxWidth: "450px",
                                marginTop: "230px",
                                padding: "0 20px",
                                position: "relative !important",
                            }}
                        >
                            <div
                                className="bumrah-container"
                                style={{
                                    minHeight: "180px",
                                    display: "flex !important",
                                    justifyContent: "center !important",
                                    alignItems: "center !important",
                                    marginBottom: "-25px !important",
                                    marginTop: "-15px !important",
                                    width: "100% !important",
                                    maxWidth: "450px !important",
                                    position: "relative !important",
                                    zIndex: "1 !important",
                                    left: "0 !important",
                                    right: "0 !important",
                                    marginLeft: "auto !important",
                                    marginRight: "auto !important",
                                }}
                            >
                                <img
                                    src="images/otpverfication/Frame162427.png"
                                    alt="Bumrah"
                                    className="bumrah-image"
                                    style={{
                                        maxWidth: "340px !important",
                                        width: "340px !important",
                                        margin: "0 auto !important",
                                        display: "block !important",
                                        height: "auto !important",
                                        transform: "scale(1) !important",
                                        position: "relative !important",
                                        left: "0 !important",
                                        right: "0 !important",
                                        zIndex: "1 !important",
                                    }}
                                />
                            </div>
                            <div
                                className="form-container"
                                style={{
                                    width: "100% !important",
                                    maxWidth: "450px !important",
                                    marginTop: "-45px !important",
                                    zIndex: "10 !important",
                                    position: "relative !important",
                                    backgroundColor: "white",
                                    borderTopLeftRadius: "15px",
                                    borderTopRightRadius: "15px",
                                    paddingTop: "30px",
                                    boxShadow: "0px -5px 15px rgba(0, 0, 0, 0.1)",
                                    left: "0 !important",
                                    right: "0 !important",
                                    marginLeft: "auto !important",
                                    marginRight: "auto !important",
                                }}
                            >
                                <MobileFormContent
                                    error={error}
                                    usingTestMode={usingTestMode}
                                    countryCode={countryCode}
                                    handleCountryCodeChange={handleCountryCodeChange}
                                    phoneNumber={phoneNumber}
                                    handlePhoneNumberChange={handlePhoneNumberChange}
                                    isLoading={isLoading}
                                    showOtpInput={showOtpInput}
                                    handleGetOtp={handleGetOtp}
                                    otp={otp}
                                    handleOtpChange={handleOtpChange}
                                    handleKeyDown={handleKeyDown}
                                    remainingTime={remainingTime}
                                    resendCooldown={resendCooldown}
                                    handleResendOtp={handleResendOtp}
                                    handleVerify={handleVerify}
                                    consentChecked={consentChecked}
                                    handleConsentChange={handleConsentChange}
                                    openConsentModal={openConsentModal}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="fixed-stepper-container" style={{ backgroundColor: "transparent" }}>
                            <div className="fixed-stepper">
                                <div className="progress-step active">
                                    <div className="step-circle">1</div>
                                    <div className="step-label">OTP</div>
                                </div>
                                <div className="progress-line active"></div>
                                <div className="progress-step">
                                    <div className="step-circle">2</div>
                                    <div className="step-label">Add Details</div>
                                </div>
                                <div className="progress-line"></div>
                                <div className="progress-step">
                                    <div className="step-circle">3</div>
                                    <div className="step-label">Upload</div>
                                </div>
                            </div>
                        </div>
                        <div className="right-content" style={{ position: "relative" }}>
                            <div className="form-container">
                                <DesktopFormContent
                                    error={error}
                                    usingTestMode={usingTestMode}
                                    countryCode={countryCode}
                                    handleCountryCodeChange={handleCountryCodeChange}
                                    phoneNumber={phoneNumber}
                                    handlePhoneNumberChange={handlePhoneNumberChange}
                                    isLoading={isLoading}
                                    showOtpInput={showOtpInput}
                                    handleGetOtp={handleGetOtp}
                                    otp={otp}
                                    handleOtpChange={handleOtpChange}
                                    handleKeyDown={handleKeyDown}
                                    remainingTime={remainingTime}
                                    resendCooldown={resendCooldown}
                                    handleResendOtp={handleResendOtp}
                                    handleVerify={handleVerify}
                                    consentChecked={consentChecked}
                                    handleConsentChange={handleConsentChange}
                                    openConsentModal={openConsentModal}
                                    handleChangePhoneNumber={handleChangePhoneNumber}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
            {showConsentModal && (
                <div
                    className={`consent-modal ${showOtpInput ? "consent-modal-over-otp" : ""}`}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 10005 /* Increased z-index to be higher than OTP modal */,
                        backdropFilter: "blur(2px)",
                    }}
                >
                    <div
                        className="consent-modal-content"
                        style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            padding: "25px",
                            width: "90%",
                            maxWidth: "450px",
                            maxHeight: "80vh",
                            overflowY: "auto",
                            boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
                        }}
                    >
                        <h3
                            className="consent-modal-title"
                            style={{
                                fontSize: "20px",
                                fontWeight: "bold",
                                marginBottom: "15px",
                                color: "#0a1a34",
                                textAlign: "center",
                            }}
                        >
                            Terms & Conditions
                        </h3>
                        <div
                            className="consent-modal-text"
                            style={{
                                fontSize: "14px",
                                color: "#333",
                                lineHeight: "1.5",
                                marginBottom: "20px",
                            }}
                        >
                            <p
                                style={{
                                    fontSize: "14px",
                                    display: "block",
                                    maxHeight: "none",
                                    overflow: "visible",
                                }}
                            >
                                I hereby consent to L & T Finance Limited and its affiliates to use,
                                edit, reproduce, and publish the photographs, videos, audio
                                recordings, contact numbers, and any AI-generated or
                                campaign-related content featuring me or submitted by me, for
                                marketing, promotional, and other commercial purposes related to
                                the "Bumrah X You" campaign, across any media platforms including
                                digital, print, outdoor, or broadcast, without any compensation or
                                further approval.
                            </p>
                        </div>
                        <div
                            className="consent-modal-buttons"
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "15px",
                            }}
                        >
                            <button
                                className="decline-btn"
                                onClick={declineConsent}
                                style={{
                                    flex: 1,
                                    padding: "12px",
                                    borderRadius: "30px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    border: "1px solid #ddd",
                                    backgroundColor: "#f5f5f5",
                                    color: "#333",
                                }}
                            >
                                Decline
                            </button>
                            <button
                                className="accept-btn"
                                onClick={acceptConsent}
                                style={{
                                    flex: 1,
                                    padding: "12px",
                                    borderRadius: "30px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    border: "none",
                                    backgroundColor: "#0083B5",
                                    color: "white",
                                }}
                            >
                                Accept
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isMobile && showOtpInput && (
                <div
                    className="otp-modal"
                    id="otp-verification-modal"
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 0,
                        backdropFilter: "blur(3px)",
                    }}
                >
                    <div
                        className="otp-modal-content"
                        style={{
                            backgroundColor: "white",
                            borderRadius: "15px",
                            padding: "25px",
                            width: "90%",
                            maxWidth: "330px",
                            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            position: "relative",
                        }}
                    >
                        <button
                            id="otp-modal-close-button"
                            className="otp-modal-close"
                            onClick={(e) => {
                                // Prevent event propagation and default behavior
                                e.stopPropagation();
                                e.preventDefault();

                                // Close the modal by setting state
                                setShowOtpInput(false);
                            }}
                            aria-label="Close OTP verification"
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                width: "36px" /* Increased size for better touch target */,
                                height: "36px" /* Increased size for better touch target */,
                                background: "rgba(0, 0, 0, 0.05)",
                                border: "none",
                                borderRadius: "50%",
                                fontSize: "24px",
                                lineHeight: "1",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "#666",
                                zIndex: "10003",
                                padding: "0",
                            }}
                        >
                            ×
                        </button>
                        <h3
                            className="otp-modal-title"
                            style={{
                                margin: "0 0 10px 0",
                                fontSize: "20px",
                                color: "#0a1a34",
                            }}
                        >
                            Enter OTP
                        </h3>
                        <p
                            className="otp-modal-instruction"
                            style={{
                                margin: "0 0 20px 0",
                                fontSize: "14px",
                                color: "#666",
                                textAlign: "center",
                            }}
                        >
                            Enter the 6-digit OTP sent to your mobile number
                        </p>
                        <div
                            className="otp-container"
                            style={{
                                display: "flex",
                                gap: "8px",
                                marginBottom: "20px",
                                width: "100%",
                                justifyContent: "center",
                            }}
                        >
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    id={`otp-input-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    className="otp-input border-blue"
                                    style={{
                                        width: "40px",
                                        height: "40px",
                                        textAlign: "center",
                                        fontSize: "18px",
                                        borderRadius: "8px",
                                        border: "1px solid #ddd",
                                        outline: "none",
                                    }}
                                    disabled={isLoading}
                                    autoComplete="off"
                                />
                            ))}
                        </div>
                        <div
                            className="otp-info"
                            style={{
                                width: "100%",
                                marginBottom: "20px",
                                textAlign: "center",
                            }}
                        >
                            <p
                                className="remaining-time"
                                style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666" }}
                            >
                                {remainingTime > 0
                                    ? `OTP expires in ${Math.floor(remainingTime / 60)}:${(remainingTime % 60).toString().padStart(2, '0')}`
                                    : "OTP expired"}
                            </p>
                            <div
                                className="resend-link"
                                style={{ fontSize: "14px", color: "#333" }}
                            >
                                <span>Didn't get the code?</span>
                                <button
                                    onClick={handleResendOtp}
                                    className="resend-btn"
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "#0083B5",
                                        fontWeight: 600,
                                        cursor: resendCooldown > 0 || isLoading ? "default" : "pointer",
                                        opacity: resendCooldown > 0 || isLoading ? 0.5 : 1,
                                        padding: 0,
                                        marginLeft: "5px",
                                        fontSize: "14px",
                                    }}
                                    disabled={resendCooldown > 0 || isLoading}
                                >
                                    Resend
                                </button>
                            </div>
                        </div>
                        <button
                            className="verify-btn"
                            onClick={handleVerify}
                            style={{
                                width: "100%",
                                padding: "12px",
                                borderRadius: "30px",
                                backgroundColor: "#0083B5",
                                color: "white",
                                border: "none",
                                fontWeight: 600,
                                cursor:
                                    isLoading || otp.join("").length !== 6
                                        ? "default"
                                        : "pointer",
                                opacity: isLoading || otp.join("").length !== 6 ? 0.7 : 1,
                            }}
                            disabled={isLoading || otp.join("").length !== 6}
                        >
                            {isLoading ? "Verifying..." : "Verify"}
                        </button>
                        {/* NEW: Show error or success message in modal */}
                        {error && (
                            <div id="modal-error-message" className={`message-box ${usingTestMode ? "info-message" : "error-message"}`}>{error}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Mobile form content component
const MobileFormContent = ({
    error,
    usingTestMode,
    countryCode,
    handleCountryCodeChange,
    phoneNumber,
    handlePhoneNumberChange,
    isLoading,
    showOtpInput,
    handleGetOtp,
    otp,
    handleOtpChange,
    handleKeyDown,
    remainingTime,
    resendCooldown,
    handleResendOtp,
    handleVerify,
    consentChecked,
    handleConsentChange,
    openConsentModal,
}) => (
    <>
        <div className="mobile-stepper-container form-stepper" style={{ backgroundColor: "transparent" }}>
            <div className="mobile-stepper">
                <div className="progress-step active">
                    <div className="step-circle">1</div>
                    <div className="step-label">OTP</div>
                </div>
                <div className="progress-line active"></div>
                <div className="progress-step">
                    <div className="step-circle">2</div>
                    <div className="step-label">Add Details</div>
                </div>
                <div className="progress-line"></div>
                <div className="progress-step">
                    <div className="step-circle">3</div>
                    <div className="step-label">Upload</div>
                </div>
            </div>
        </div>
        <h2 className="form-title" style={{ textAlign: "center" }}>
            Game on.
        </h2>
        <h2 className="form-subtitle" style={{ textAlign: "center" }}>
            First delivery's yours!
        </h2>
        <p className="form-description" style={{ textAlign: "center" }}>
            Enter your phone number to receive a One-Time Password(OTP) for verification.
        </p>
        {error && (
            <div
                id="error-message"
                className={`message-box ${usingTestMode ? "info-message" : "error-message"}`}
            >
                {error}
            </div>
        )}
        <div className="phone-input-group">
            <div className="input-group">
                <div className="country-phone-container">
                    <select
                        className="country-code-select border-blue"
                        value={countryCode}
                        onChange={handleCountryCodeChange}
                        disabled={isLoading || (showOtpInput && remainingTime > 0)}
                    >
                        <option value="+91">🇮🇳+91</option>
                        <option value="+1">🇺🇸+1</option>
                        <option value="+44">🇬🇧+44</option>
                        <option value="+61">🇦🇺+61</option>
                        <option value="+971">🇦🇪+971</option>
                    </select>
                    <input
                        type="tel"
                        placeholder="Enter your mobile number."
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        className="phone-input border-blue"
                        maxLength={10}
                        disabled={isLoading || (showOtpInput && resendCooldown > 0)}
                    />
                </div>
                <button
                    className="get-otp-btn"
                    onClick={handleGetOtp}
                    disabled={isLoading || phoneNumber.length !== 10 || (showOtpInput && resendCooldown > 0)}
                >
                    {isLoading && !showOtpInput ? "Sending..." : "Get OTP"}
                </button>
            </div>
        </div>
        <div className="consent-container" style={{ display: "flex", alignItems: "flex-start", margin: "10px 0" }}>
            <div style={{ display: "flex", alignItems: "flex-start" }}>
                <div
                    onClick={(e) => {
                        e.preventDefault();
                        openConsentModal(e);
                    }}
                    style={{
                        width: "10px",
                        height: "10px",
                        minWidth: "10px",
                        border: "1px solid #999",
                        borderRadius: "2px",
                        marginRight: "8px",
                        marginTop: "3px",
                        backgroundColor: consentChecked ? "#0a1a34" : "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: isLoading ? "default" : "pointer",
                        opacity: isLoading ? 0.5 : 1,
                    }}
                >
                    {consentChecked && (
                        <div
                            style={{
                                width: "4px",
                                height: "6px",
                                borderRight: "2px solid white",
                                borderBottom: "2px solid white",
                                transform: "rotate(45deg) translate(-1px, -1px)",
                            }}
                        />
                    )}
                </div>
                <span
                    style={{ fontSize: "12px", lineHeight: "1.3", cursor: "pointer" }}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!isLoading) {
                            openConsentModal(e);
                        }
                    }}
                >
                    By continuing, I accept the{" "}
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            openConsentModal(e);
                        }}
                    >
                        Terms & Conditions
                    </a>
                </span>
            </div>
        </div>
    </>
);

// Desktop form content component - Updated to remove bypass mode
const DesktopFormContent = ({
    error,
    usingTestMode,
    countryCode,
    handleCountryCodeChange,
    phoneNumber,
    handlePhoneNumberChange,
    isLoading,
    showOtpInput,
    handleGetOtp,
    otp,
    handleOtpChange,
    handleKeyDown,
    remainingTime,
    resendCooldown,
    handleResendOtp,
    handleVerify,
    consentChecked,
    handleConsentChange,
    openConsentModal,
    handleChangePhoneNumber,
}) => (
    <>
        <h2 className="form-title">Game on.</h2>
        <h2 className="form-subtitle">First delivery's yours!</h2>
        <p className="form-description">
            Enter your phone number to receive a One-Time Password(OTP) for verification.
        </p>
        {error && (
            <div
                id="error-message"
                className={`message-box ${usingTestMode ? "info-message" : "error-message"}`}
            >
                {error}
            </div>
        )}
        <div className="phone-input-group">
            <div className="input-group">
                <div className="country-phone-container">
                    <select
                        className="country-code-select border-blue"
                        value={countryCode}
                        onChange={handleCountryCodeChange}
                        disabled={isLoading || (showOtpInput && remainingTime > 0)}
                    >
                        <option value="+91">🇮🇳+91</option>
                        <option value="+1">🇺🇸+1</option>
                        <option value="+44">🇬🇧+44</option>
                        <option value="+61">🇦🇺+61</option>
                        <option value="+971">🇦🇪+971</option>
                    </select>
                    <input
                        type="tel"
                        placeholder="Enter your mobile number."
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        className="phone-input border-blue"
                        maxLength={10}
                        disabled={isLoading || (showOtpInput && resendCooldown > 0)}
                    />
                </div>
                <button
                    className="get-otp-btn"
                    onClick={handleGetOtp}
                    disabled={isLoading || phoneNumber.length !== 10 || (showOtpInput && resendCooldown > 0)}
                >
                    {isLoading && !showOtpInput ? "Sending..." : "Get OTP"}
                </button>
            </div>
        </div>
        {showOtpInput && (
            <div className="simple-otp-container">
                <div className="otp-container">
                    {otp.map((digit, index) => (
                        <input
                            key={index}
                            id={`otp-input-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className="otp-input border-blue"
                            disabled={isLoading}
                            autoComplete="off"
                        />
                    ))}
                </div>
                <div className="otp-info">
                    <p className="remaining-time">
                        {remainingTime > 0
                            ? `OTP expires in ${Math.floor(remainingTime / 60)}:${(remainingTime % 60).toString().padStart(2, '0')}`
                            : "OTP expired"}
                    </p>
                    <div className="resend-link">
                        <span>
                            {resendCooldown > 0 
                                ? `Resend available in ${resendCooldown}s` 
                                : "Didn't get the code?"
                            }
                        </span>
                        <button
                            onClick={handleResendOtp}
                            className="resend-btn"
                            disabled={resendCooldown > 0 || isLoading}
                        >
                            Resend
                        </button>
                    </div>
                </div>
                <button
                    className="verify-btn"
                    onClick={handleVerify}
                    disabled={isLoading || otp.join("").length !== 6}
                >
                    {isLoading ? "Verifying..." : "Verify"}
                </button>
            </div>
        )}
        <div className="consent-container" style={{ display: "flex", alignItems: "flex-start", margin: "10px 0" }}>
            <div style={{ display: "flex", alignItems: "flex-start" }}>
                <div
                    onClick={(e) => {
                        if (!isLoading) {
                            openConsentModal(e);
                        }
                    }}
                    style={{
                        width: "12px",
                        height: "12px",
                        minWidth: "12px",
                        border: "1px solid #0083B5",
                        borderRadius: "2px",
                        marginRight: "8px",
                        marginTop: "3px",
                        backgroundColor: consentChecked ? "#0a1a34" : "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: isLoading ? "default" : "pointer",
                        opacity: isLoading ? 0.5 : 1,
                    }}
                >
                    {consentChecked && (
                        <div
                            style={{
                                width: "4px",
                                height: "6px",
                                borderRight: "2px solid white",
                                borderBottom: "2px solid white",
                                transform: "rotate(45deg) translate(-1px, -1px)",
                            }}
                        />
                    )}
                </div>
                <span
                    style={{ fontSize: "12px", lineHeight: "1.3", cursor: "pointer" }}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!isLoading) {
                            openConsentModal(e);
                        }
                    }}
                >
                    By continuing, I accept the{" "}
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            openConsentModal(e);
                        }}
                    >
                        Terms & Conditions
                    </a>
                </span>
            </div>
        </div>
    </>
);

export default OtpVerification;